import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import apis from "../../../context/api";

// eslint-disable-next-line react/prop-types
function ComboEdit({ id, onSuccess, onCancel }) {
  const [products, setProducts] = useState([]);
  const [comboData, setComboData] = useState({
    name: "",
    shortName: "",
    productItems: [], // [{ productId, quantity }]
    sellingPrice: 1,
    discount: 0,
    minStockQty: 1,
    totalQuantity: 1,
    buyPrice: 1,
  });

  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);

  const validateFields = () => {
    const newErrors = {};
    if (!comboData.name.trim()) newErrors.name = "Name is required";
    if (!comboData.shortName.trim()) newErrors.shortName = "Short Code is required";
    if (!comboData.sellingPrice) newErrors.sellingPrice = "Selling Price is required";
    if (!comboData.buyPrice) newErrors.buyPrice = "Buy Price is required";
    if (comboData.productItems.length === 0) newErrors.productItems = "Product Items is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // returns true if no errors
  };

  // Load products
  useEffect(() => {
    apis
      .getProducts()
      .then((data) => {
        setProducts(data);
      })
      .catch(() => setError("Failed to load products"));
    const selectProducts = comboData.productItems
      .map((p) => products.find((prod) => prod.id === p.productId))
      .filter(Boolean);
    setSelectedProducts(selectProducts);
  }, []);

  // Load combo if editing
  useEffect(() => {
    if (!id) {
      setComboData({
        name: "",
        shortName: "",
        productItems: [],
        sellingPrice: 0,
        discount: 1,
        minStockQty: 1,
        totalQuantity: 1,
        buyPrice: 1,
      });
      return;
    }
    apis
      .getCombosById(id)
      .then((data) => {
        let productItemsData = [];
        if (data.products) {
          data.products.forEach((p) => {
            productItemsData.push({
              productId: p.id,
              quantity: p.quantity,
              productShortName: p.productShortName,
            });
          });
        }
        setComboData({
          name: data.name,
          shortName: data.shortName,
          sellingPrice: data.sellingPrice,
          discount: data.discount,
          minStockQty: data.minStockQty,
          totalQuantity: data.totalQuantity,
          productItems: productItemsData,
          buyPrice: data.buyPrice,
        });
        let selectProducts = comboData.productItems
          .map((p) => products.find((prod) => prod.id === p.productId))
          .filter(Boolean);
        setSelectedProducts(selectProducts);
      })
      .catch((e) => setError("Failed to load combo" + e));
  }, [id]);

  useEffect(() => {
    if (comboData.productItems.length > 0 && products.length > 0) {
      const selected = comboData.productItems
        .map((item) => products.find((p) => p.id === item.productId))
        .filter(Boolean);
      setSelectedProducts(selected);
    }
  }, [comboData.productItems, products]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if ((name === "minStockQty" || name === "totalQuantity") && Number(value) < 1) {
      newValue = "1";
    }

    setComboData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleProductSelect = (event, values) => {
    const updatedItems = values.map((product) => {
      const existing = comboData.productItems.find((p) => p.productId === product.id);
      return {
        productId: product.id,
        quantity: existing?.quantity || 1,
      };
    });
    setComboData((prev) => ({ ...prev, productItems: updatedItems }));
  };

  const handleQuantityChange = (productId, quantity) => {
    const updated = comboData.productItems.map((item) =>
      item.productId === productId ? { ...item, quantity } : item
    );
    setComboData((prev) => ({ ...prev, productItems: updated }));
  };

  const removeProduct = (productId) => {
    const filtered = comboData.productItems.filter((item) => item.productId !== productId);
    setComboData((prev) => ({ ...prev, productItems: filtered }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;
    const method = id ? "PUT" : "POST";
    const url = id ? `combos/${id}` : `combos`;

    try {
      apis
        .comboUpdateOrCreate(comboData, method, url)
        .then((data) => {
          if (onSuccess) onSuccess(data);
        })
        .catch((err) => {
          setError(err.message);
        });
    } catch (err) {
      setError(err.message);
    }
  };
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <MDBox pt={6} pb={6}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={5}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Combo Name"
                    name="name"
                    value={comboData.name}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name}
                    FormHelperTextProps={{
                      sx: { color: "red" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Combo Short Code"
                    name="shortName"
                    value={comboData.shortName}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.shortName}
                    helperText={errors.shortName}
                    FormHelperTextProps={{
                      sx: { color: "red" },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={products}
                    getOptionLabel={(option) => option.productShortName || ""}
                    value={selectedProducts}
                    onChange={handleProductSelect}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Products by Short Name"
                        error={!!errors.productItems}
                        helperText={errors.productItems}
                        FormHelperTextProps={{
                          sx: { color: "red" },
                        }}
                      />
                    )}
                    fullWidth
                  />
                </Grid>

                {comboData.productItems.map((item) => {
                  const product = products.find((p) => p.id === item.productId);
                  return (
                    <Grid
                      container
                      spacing={1}
                      p={2}
                      key={item.productId}
                      alignItems="center"
                      mt={1}
                    >
                      <Grid item xs={6}>
                        <MDInput
                          label={product?.productShortName || "Product"}
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(item.productId, parseInt(e.target.value))
                          }
                          type="number"
                          fullWidth
                        />
                      </Grid>
                      <Grid item>
                        <IconButton onClick={() => removeProduct(item.productId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  );
                })}

                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Selling Price"
                    name="sellingPrice"
                    value={comboData.sellingPrice}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    error={!!errors.sellingPrice}
                    helperText={errors.sellingPrice}
                    FormHelperTextProps={{
                      sx: { color: "red" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Buy Price"
                    name="buyPrice"
                    value={comboData.buyPrice}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                    error={!!errors.buyPrice}
                    helperText={errors.buyPrice}
                    FormHelperTextProps={{
                      sx: { color: "red" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Minimum Stock Quantity"
                    name="minStockQty"
                    value={comboData.minStockQty}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Total Quantity"
                    name="totalQuantity"
                    value={comboData.totalQuantity}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Discount"
                    name="discount"
                    value={comboData.discount}
                    onChange={handleChange}
                    type="number"
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Grid
                container
                justifyContent="flex-end"
                pt={4}
                spacing={1}
                display="flex"
                flexWrap="nowrap"
              >
                <Grid item pl={2} pr={2} xs="auto">
                  <MDButton variant="gradient" color="warning" onClick={handleCancel}>
                    Cancel
                  </MDButton>
                </Grid>
                <Grid item xs="auto">
                  <MDButton variant="gradient" color="info" onClick={handleSubmit}>
                    {id ? "Update Combo" : "Create Combo"}
                  </MDButton>
                </Grid>
              </Grid>

              {error && (
                <MDBox mt={2} color="error">
                  <strong>{error}</strong>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default ComboEdit;
